<template>
  <div>
    <div v-if="!support" class="text-center">
      <h3 class="text-danger">
        Your browser does not support video recording.
      </h3>
    </div>
    <div v-if="support" ref="video-wrapper" class="video-wrapper">
      <div
        v-show="state === states.COUNTING_DOWN"
        class="countdown position-absolute inset-0 w-100 h-100"
      >
        <span class="text-white position-absolute inset-0 w-50px h-fit m-auto text-center">{{ countdownSec }}</span>
      </div>
      <div
        v-if="!webcamReady"
        class="tw:absolute tw:size-fit tw:inset-0 tw:top-1/2 tw:left-1/2 tw:text-white tw:-translate-x-1/2 tw:-translate-y-1/2"
      >
        <span class="fa fa-spinner fa-spin fa-3x" />
      </div>
      <canvas
        ref="canvasRef"
        class="w-100 cursor-move"
        :width="webcamOutputCanvasSize.width"
        :height="webcamOutputCanvasSize.height"
      />
      <WebcamRecorderStartRecordingButton
        v-if="state === states.READY && permissionGranted && webcamReady"
        class="dubb-video-preview-btn tw:inset-0 tw:absolute tw:m-auto"
        @click="countdown()"
      />
      <div v-if="duration" class="duration-wrapper position-absolute mx-auto left-0 right-0 w-fit rounded">
        <span class="text-white m-1">{{ duration }}</span>
      </div>
      <div
        v-if="state === states.RECORDING || state === states.PAUSED || state === states.COUNTING_DOWN"
        class="tw:flex tw:items-end tw:justify-center tw:gap-2 tw:absolute tw:bottom-0 tw:inset-x-0 tw:mx-auto tw:mb-4"
      >
        <WebcamRecorderResumeRecordingButton
          v-if="state === states.PAUSED"
          class="dubb-video-preview-btn"
          @click="resume()"
        />

        <WebcamRecorderPauseRecordingButton
          v-else
          class="dubb-video-preview-btn"
          @click="pause()"
        />

        <WebcamRecorderCancelRecordingButton
          class="dubb-video-preview-btn"
          @click="cancel()"
        />

        <WebcamRecorderUploadRecordingButton
          class="dubb-video-preview-btn"
          @click="stop()"
        />
      </div>
      <div v-if="simple && webcamReady && state === states.READY" class="setting-toggler position-absolute d-flex justify-content-center align-items-center m-4 bottom-0 right-0 p-1">
        <a class="btn btn-transparent btn-xs btn-icon cursor-pointer" @click="showSettings = !showSettings"><i class="fal fa-cogs" /></a>
      </div>

      <WebcamRecorderTransformControls class="tw:bg-black/50 tw:absolute tw:left-0 tw:bottom-0 tw:m-4" />
    </div>

    <form v-show="showSettings && state === states.READY && permissionGranted" class="mt-4">
      <div class="row">
        <div class="form-group col-sm-4">
          <label>
            WebCam
            <CustomTooltip
              text="The Dubb video system uses your computer's camera in order to record an asynchronous video. You will need to grant access the first time you use the system in your System Preferences. If you are having trouble connecting your camera, please read this support article."
              read-more="https://support.dubb.com/en/articles/5764910-how-to-allow-webcam-and-mic-access-to-the-dubb-chrome-extension"
            />
          </label>
          <select v-model="webcamDeviceId" class="form-control">
            <option v-for="item in webcamDevices" :value="item.deviceId">
              {{ item.label }}
            </option>
          </select>
        </div>
        <div class="form-group col-sm-4">
          <div style="display: flex; flex-direction: row; justify-content: space-between; align-items: center;">
            <label>
              Microphone
              <CustomTooltip
                text="The Dubb video system uses your computer's microphone in order to record an asynchronous video. You will need to grant access the first time you use the system in your System Preferences. If you are having trouble connecting your microphone, please read this support article."
                read-more="https://support.dubb.com/en/articles/5764910-how-to-allow-webcam-and-mic-access-to-the-dubb-chrome-extension"
              />
            </label>
            <div v-if="!simple" class="progress tw:h-2 tw:flex-1 tw:mb-0 tw:ml-3">
              <div class="bg-success tw:h-full" :style="{ width: `${audioVolume}%` }" />
            </div>
          </div>
          <select v-model="microphoneDeviceId" class="form-control">
            <option v-for="item in microphoneDevices" :value="item.deviceId">
              {{ item.label }}
            </option>
          </select>
        </div>
        <div v-if="!simple" class="form-group col-sm-4">
          <label>
            Quality
            <CustomTooltip
              text="Higher resolution results in slower uploading and transcoding speeds."
            />
          </label>
          <select v-model="quality" class="form-control" :disabled="guest">
            <option v-for="item in qualities" :value="item.value">
              {{ item.label }}
            </option>
          </select>
        </div>
      </div>
      <div v-if="!simple" class="row">
        <div class="col-md-4">
          <CheckboxField
            v-model="isMirrorVideo"
            label="Mirror my video"
          />
        </div>
        <div v-if="!isHideTeleprompter" class="col-md-4">
          <CheckboxField
            v-model="isDisplayTeleprompter"
            label="Display Teleprompter"
          />
        </div>
      </div>
      <div v-if="!simple" class="row">
        <div class="col-md-4 d-flex align-items-center">
          <CheckboxField
            v-model="isVirtualBackgroundBlur"
            label="Blur Background"
            :disabled="!webcamReady"
            :tooltip-options="{
              text: 'This feature allows you to blur your existing background. Here are minimum system requirements: MAC OS 13, Windows 10 64 bit, 8GB of ram, and i7 dual core CPU.',
            }"
          />
        </div>
        <div class="col-md-4 d-flex align-items-center">
          <CheckboxField
            v-model="isVirtualBackgroundImage"
            label="Virtual Background"
            :disabled="!webcamReady"
            :tooltip-options="{
              text: 'This feature allows you to add virtual backgrounds. Here are minimum system requirements: MAC OS 13, Windows 10 64 bit, 8GB of ram, and i7 dual core CPU.',
              readMore: 'https://support.dubb.com/en/articles/5764788-how-to-use-dubb-virtual-backgrounds',
            }"
          />
        </div>
      </div>
      <VirtualBackgroundImagePicker
        v-if="!simple"
        v-show="isVirtualBackgroundImage"
        v-model="virtualBackgroundImage"
        class="mt-3"
        :visible="isVirtualBackgroundImage"
        :guest="guest"
      />
    </form>
    <Teleprompter v-if="support && isDisplayTeleprompter && !isHideTeleprompter && teamHasFeature(constants.GLOBAL.FEATURE_TELEPROMPTER_SCRIPT)" ref="teleprompter" />
  </div>
</template>

<script>
import { useDevicesList } from '@vueuse/core'
import last from 'lodash/last'
import { Duration } from 'luxon'
import raf from 'raf'
import RecordRTC from 'recordrtc'
import { nextTick, watch } from 'vue'
import CustomTooltip from '@/components/custom-tooltip.vue'
import { CheckboxField } from '@/shadcn/components/ui/checkbox'
import { is_touchable_device } from '@/utils'
import * as icons from '../../common/icons'
import RecorderMixin from './recorder-mixin'
import Teleprompter from './teleprompter/teleprompter.vue'
import VirtualBackgroundImagePicker from './virtual-background-image-picker.vue'
import { WEBCAM_QUALITIES, WEBCAM_QUALITY_AUTO } from './webcam-recorder/constants.js'
import { useWebcamRecorder } from './webcam-recorder/use-webcam-recorder'
import { useWebcamRecorderCanvas } from './webcam-recorder/use-webcam-recorder-canvas'
import WebcamRecorderCancelRecordingButton from './webcam-recorder/webcam-recorder-cancel-recording-button.vue'
import WebcamRecorderPauseRecordingButton from './webcam-recorder/webcam-recorder-pause-recording-button.vue'
import WebcamRecorderResumeRecordingButton from './webcam-recorder/webcam-recorder-resume-recording-button.vue'
import WebcamRecorderStartRecordingButton from './webcam-recorder/webcam-recorder-start-recording-button.vue'
import WebcamRecorderTransformControls from './webcam-recorder/webcam-recorder-transform-controls.vue'
import WebcamRecorderUploadRecordingButton from './webcam-recorder/webcam-recorder-upload-recording-button.vue'
import 'webrtc-adapter'

window.RecordRTC = RecordRTC

const states = {
  READY: 'ready',
  COUNTING_DOWN: 'counting_down',
  RECORDING: 'recording',
  PAUSED: 'paused',
}

const FPS = 30

export default {
  name: 'WebcamRecorder',
  components: {
    CheckboxField,
    WebcamRecorderTransformControls,
    WebcamRecorderPauseRecordingButton,
    WebcamRecorderResumeRecordingButton,
    WebcamRecorderCancelRecordingButton,
    WebcamRecorderUploadRecordingButton,
    WebcamRecorderStartRecordingButton,
    VirtualBackgroundImagePicker,
    Teleprompter,
    CustomTooltip,
  },
  mixins: [RecorderMixin],
  props: {
    guest: {
      type: Boolean,
      default: false,
    },
    simple: {
      type: Boolean,
      default: false,
    },
    hideTeleprompter: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['record-finished', 'ready', 'unsupported'],
  setup() {
    const {
      videoInputs: webcamDevices,
      audioInputs: microphoneDevices,
      ensurePermissions,
      permissionGranted,
    } = useDevicesList()

    const {
      webcamDeviceId,
      microphoneDeviceId,
      videoResolution,
      isMirrorVideo,
      isDisplayTeleprompter,
      isVirtualBackgroundBlur,
      isVirtualBackgroundImage,
      virtualBackgroundImage,

      webcamScale,
      webcamTranslate,

      canvasRef,

      audioRecordingStream,
      audioVolume,

      webcamRecordingStream,
      webcamOutputCanvasSize,
      virtualBackgroundImageUrl,
      handelWebcamError,
    } = useWebcamRecorder()

    useWebcamRecorderCanvas()

    watch(webcamDevices, (newDevices) => {
      if (!webcamDeviceId.value && newDevices.length > 0) {
        webcamDeviceId.value = newDevices[0].deviceId
      }
    })

    watch(microphoneDevices, (newDevices) => {
      if (!microphoneDeviceId.value && newDevices.length > 0) {
        microphoneDeviceId.value = newDevices[0].deviceId
      }
    })

    return {
      webcamDeviceId,
      microphoneDeviceId,
      videoResolution,
      isMirrorVideo,
      isDisplayTeleprompter,
      isVirtualBackgroundBlur,
      isVirtualBackgroundImage,
      virtualBackgroundImage,

      webcamScale,
      webcamTranslate,

      canvasRef,

      audioRecordingStream,
      audioVolume,

      webcamRecordingStream,
      webcamOutputCanvasSize,
      virtualBackgroundImageUrl,

      webcamDevices,
      microphoneDevices,

      ensurePermissions,
      permissionGranted,
      handelWebcamError,
    }
  },
  data: () => ({
    icons,
    states,
    showSettings: true,
    countdownSec: 3,
    duration: null,
    timeParts: [],
    state: null,
    support: true,
    webcamReady: false,
    currentRaf: null,
    quality: WEBCAM_QUALITY_AUTO,
    lastTimestamp: null,
    captureAborted: false,
  }),
  computed: {
    isGuestOrSimple() {
      return this.guest || this.simple
    },
    isHideTeleprompter() {
      return this.isGuestOrSimple || this.hideTeleprompter
    },
    qualities() {
      if (this.isGuestOrSimple) {
        return [last(WEBCAM_QUALITIES)]
      }

      return WEBCAM_QUALITIES
    },
  },
  watch: {
    quality: {
      handler(newVal) {
        if (!newVal) {
          return
        }

        this.webcamRecordingStream.setResolution(newVal).catch(this.handelWebcamError)

        if (!this.isGuestOrSimple) {
          this.videoResolution = newVal
        }
      },
      immediate: true,
    },
  },

  created() {
    this.showSettings = !this.simple
    if (this.isGuestOrSimple) {
      this.quality = last(WEBCAM_QUALITIES).value
    }
  },
  mounted() {
    if (!navigator.mediaDevices || is_touchable_device() || typeof MediaRecorder !== 'function') {
      this.support = false
      this.$emit('unsupported')
      return
    }
    const quality = this.videoResolution
    if (!this.guest && quality) {
      const qualityExists = WEBCAM_QUALITIES.some(q => q.value === quality)
      this.quality = qualityExists ? quality : WEBCAM_QUALITY_AUTO
    }

    this.checkAndLoadScriptFromQuery()

    this.$emit('ready')
  },
  methods: {
    checkAndLoadScriptFromQuery() {
      const params = new URLSearchParams(window.location.search)
      const scriptId = params.get('script_id')
      if (scriptId && !this.isHideTeleprompter) {
        this.isDisplayTeleprompter = true
        nextTick(() => {
          const teleprompter = this.$refs.teleprompter
          if (teleprompter) {
            teleprompter.loadScript(scriptId)
          }
        })
      }
    },
    capture(cb = null) {
      if (!this.support) {
        return
      }

      this.captureAborted = false

      nextTick(async () => {
        const shouldDelay = !this.permissionGranted
        const hasPermissions = await this.ensurePermissions()
        if (!hasPermissions) {
          return
        }

        // when the permission is not granted before, we need to wait for a moment
        // to let the webcam stream totally cleanup after the permission prompt
        if (shouldDelay) {
          await new Promise(resolve => setTimeout(resolve, 100))
        }

        this.webcamReady = false

        try {
          this.audioRecordingStream.stop()
          await this.webcamRecordingStream.stop()
        }
        catch (err) {}

        this.audioRecordingStream.start()
        try {
          await this.webcamRecordingStream.start()
        }
        catch (err) {
          this.handelWebcamError(err)
        }

        // Check if this capture was aborted (stopCapture was called)
        if (this.captureAborted) {
          this.audioRecordingStream.stop()
          this.webcamRecordingStream.stop()
          return
        }

        this.setState(this.states.READY)
        this.webcamReady = true

        if (this.currentRaf) {
          raf.cancel(this.currentRaf)
        }

        this.drawOffscreenCanvas()

        if (cb) {
          cb()
        }
      })
    },
    stopCapture() {
      if (!this.support) {
        return
      }

      this.captureAborted = true

      this.setState(null)

      this.audioRecordingStream.stop()
      this.webcamRecordingStream.stop()
    },
    async countdown() {
      if (!this.permissionGranted) {
        await this.ensurePermissions()
        await this.countdown()

        return
      }

      this.setState(states.COUNTING_DOWN)

      this.countdownSec = 3
      const hdl = setInterval(() => {
        if (this.countdownSec === 0) {
          clearInterval(hdl)
          this.start()

          return
        }
        this.countdownSec--
      }, 1000)
    },
    start() {
      if (this.state !== states.COUNTING_DOWN) {
        return
      }

      const stream = this.canvasRef.captureStream(FPS)
      if (this.audioRecordingStream.stream) {
        const audioTracks = this.audioRecordingStream.stream.getAudioTracks()
        audioTracks.forEach((track) => {
          stream.addTrack(track)
        })
      }

      if (!stream) {
        this.support = false
        return
      }

      this.startRecorder(stream)

      this.timeParts = [{ start: new Date().getTime() }]
      this.setState(states.RECORDING)
      this.refreshDuration()
      this.resumeTeleprompter()
    },

    async cancel() {
      this.pause()
      const res = await window.SwalConfirm.fire({
        icon: 'warning',
        title: 'Are you sure?',
        confirmButtonText: 'Yes, cancel this recording',
        cancelButtonText: 'No',
      })

      if (res.isConfirmed) {
        await this.stop(false)
        return
      }

      this.resume()
    },
    async stop(save = true) {
      if (this.state !== states.PAUSED && this.state !== states.RECORDING) {
        return
      }

      const file = await this.stopRecorder(save)
      if (save) {
        this.$emit('record-finished', file, this.constants.App_Models_Video.SOURCE_TYPE_RECORD)
      }

      this.setState(states.READY)
      this.pauseTeleprompter()
    },
    pause() {
      if (this.state !== states.RECORDING) {
        return
      }
      this.recorder.pauseRecording()
      this.setState(states.PAUSED)
      const lastPart = this.timeParts[this.timeParts.length - 1]
      lastPart.end = new Date().getTime()
      lastPart.duration = lastPart.end - lastPart.start
      this.pauseTeleprompter()
    },
    resume() {
      if (this.state !== states.PAUSED) {
        return
      }
      this.recorder.resumeRecording()
      this.setState(states.RECORDING)
      this.timeParts.push({ start: new Date().getTime() })
      this.resumeTeleprompter()
    },
    setState(state) {
      this.state = state
    },

    refreshDuration() {
      if (this.state !== states.RECORDING && this.state !== states.PAUSED) {
        this.duration = null
        this.timeParts = []
        return
      }

      this.duration = this.calculateTimeDuration(this.timeParts.reduce((carry, part) => {
        return carry + (part.duration ? part.duration : (new Date().getTime() - part.start))
      }, 0) / 1000)
      setTimeout(() => {
        this.refreshDuration()
      }, 500)
    },
    calculateTimeDuration(secs) {
      const duration = Duration.fromObject({ seconds: secs })

      if (duration.hours <= 0) {
        return duration.toFormat('mm:ss')
      }

      return duration.toFormat('hh:mm:ss')
    },

    resumeTeleprompter() {
      const ref = this.$refs.teleprompter
      if (ref && ref.scriptSelected) {
        ref.scroll()
      }
    },

    pauseTeleprompter() {
      const ref = this.$refs.teleprompter
      if (ref && ref.scriptSelected) {
        ref.pause()
      }
    },

    drawOffscreenCanvas() {
      if (!this.canvasRef) {
        return
      }

      const canvasElement = this.canvasRef
      const ctx = canvasElement.getContext('2d')

      ctx.clearRect(0, 0, canvasElement.width, canvasElement.height)

      ctx.save()
      const translateX = this.webcamTranslate.x
      const translateY = this.webcamTranslate.y
      const scaleX = this.webcamScale
      const scaleY = this.webcamScale

      ctx.scale(scaleX, scaleY)
      ctx.translate(translateX, translateY)

      ctx.drawImage(
        this.webcamRecordingStream.outputCanvasElement,
        0,
        0,
      )

      ctx.restore()

      this.currentRaf = raf(() => {
        this.drawOffscreenCanvas()
      })
    },

  },
}
</script>

<style scoped lang="scss">
  .video-wrapper {
    position: relative;

    & > div {
      z-index: 97;

      &.setting-toggler {
        background: rgba(0,0,0,0.5);
      }
    }

    .countdown {
      background: rgba(0, 0, 0, .5);

      span {
        font-size: 42px;
      }
    }

    .duration-wrapper {
      bottom: 70px;
      background: rgba(0, 0, 0, .5);
      span {
        font-size: 24px;
      }
    }

    canvas {
      background-color: black;
    }
  }

  .player {
    width: 100%;
    height: 100%;
    background: black;
  }

  :deep() {
    .dubb-video-preview-btn {
      width: 36px;
      height: 36px;
      line-height: 16px;
      background-color: #e7e7e7;
      border: none;
      color: black;
      text-align: center;
      text-decoration: none;
      display: inline-flex;
      justify-content: center;
      align-items: center;
      font-size: 16px;
      cursor: pointer;
      border-radius: 50% !important;

      &:hover {
        background: white;
      }
    }
  }
</style>
